import {
    Stack,
    PrimaryButton,
    Icon,
    Text,
    MessageBar,
    MessageBarType,
    Spinner,
    SpinnerSize,
    Pivot,
    PivotItem,
    TextField,
} from '@fluentui/react';
import React, { useCallback, useContext, useRef, useState } from 'react';
import { usePromise } from '../../../../Common/hooks/usePromise';
import { replayMessagesByIds } from '../../../../AGCP/api/AGCPApi';
import {
    parseMessageIdsFromCsv,
    parseMessageIdsFromText,
    MessageIdCsvParseResult,
} from '../../../../AGCP/utils/CsvUtils';
import { ThemeContext } from '../../../../HubLayout/models/ThemeContext';
import { getThemeFromString } from '../../../../Common/util/localStorageUtils';

interface IReplayMessagesProps {}

type InputMode = 'csv' | 'paste';

// Recommended maximum number of ids to replay in a single request.
const RECOMMENDED_MAX_IDS = 1000;

export const ReplayMessages: React.FC<IReplayMessagesProps> = (
    props: IReplayMessagesProps
) => {
    const themeContext = useContext(ThemeContext);
    const theme = getThemeFromString(themeContext.themeName);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [inputMode, setInputMode] = useState<InputMode>('csv');

    const [fileName, setFileName] = useState<string>('');
    const [parseResult, setParseResult] =
        useState<MessageIdCsvParseResult | null>(null);
    const [fileReadError, setFileReadError] = useState<string | null>(null);
    const [isDragging, setIsDragging] = useState<boolean>(false);

    const [pasteText, setPasteText] = useState<string>('');
    const [pasteResult, setPasteResult] =
        useState<MessageIdCsvParseResult | null>(null);

    const [isLoading, setIsLoading] = useState<boolean>(false);

    const stackTokens = { childrenGap: 16 };

    const processFile = useCallback((file: File) => {
        setFileReadError(null);
        setParseResult(null);

        if (!file.name.toLowerCase().endsWith('.csv')) {
            setFileName('');
            setFileReadError('Please select a CSV file (.csv).');
            return;
        }

        setFileName(file.name);

        const reader = new FileReader();
        reader.onload = (e) => {
            const text = e.target?.result as string;
            if (!text || text.trim() === '') {
                setFileReadError('The selected file is empty.');
                return;
            }
            setParseResult(parseMessageIdsFromCsv(text));
        };
        reader.onerror = () => {
            setFileReadError('Failed to read the file. Please try again.');
        };
        reader.readAsText(file);
    }, []);

    const handleFileSelect = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0];
            if (file) {
                processFile(file);
            }
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        },
        [processFile]
    );

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    }, []);

    const handleDrop = useCallback(
        (e: React.DragEvent) => {
            e.preventDefault();
            e.stopPropagation();
            setIsDragging(false);
            const file = e.dataTransfer.files?.[0];
            if (file) {
                processFile(file);
            }
        },
        [processFile]
    );

    const handlePasteChange = useCallback(
        (
            event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
            newValue?: string
        ) => {
            const value = newValue ?? '';
            setPasteText(value);
            setPasteResult(
                value.trim() === '' ? null : parseMessageIdsFromText(value)
            );
        },
        []
    );

    const handleModeChange = useCallback((item?: PivotItem) => {
        const mode = (item?.props.itemKey as InputMode) ?? 'csv';
        setInputMode(mode);
    }, []);

    const activeResult = inputMode === 'csv' ? parseResult : pasteResult;
    const ids = activeResult?.ids ?? [];

    const replayCallback = useCallback(async () => {
        if (ids.length > 0) {
            try {
                const result = await replayMessagesByIds(ids);
                // Clear the input so a new list must be provided before
                // replaying again (prevents accidentally re-running the same
                // ids). Only done on success so a failed run can be retried.
                setParseResult(null);
                setFileName('');
                setFileReadError(null);
                setPasteText('');
                setPasteResult(null);
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }
                return result;
            } finally {
                // Always reset loading so the button re-enables even if the
                // request rejects.
                setIsLoading(false);
            }
        }
    }, [ids]);

    const [result, error, isLoaded, start, cancel] = usePromise(
        replayCallback,
        false
    );

    const handleReplayClick = () => {
        setIsLoading(true);
        start();
    };

    return (
        <Stack tokens={stackTokens}>
            {/* Header / instructions */}
            <MessageBar
                messageBarType={MessageBarType.info}
                styles={{
                    root: {
                        backgroundColor: theme.palette.themeLighter,
                    },
                }}
            >
                <Text
                    variant="small"
                    styles={{
                        root: {
                            fontWeight: 600,
                            display: 'block',
                            marginBottom: '4px',
                        },
                    }}
                >
                    Replay Messages
                </Text>
                <Text variant="small" styles={{ root: { display: 'block' } }}>
                    Re-publish Air Gap Messages to the Event Grid by their
                    MessageId. Provide the ids by uploading a dgrep CSV export
                    (with a &quot;MessageId&quot; column) or by pasting a list
                    of ids directly.
                </Text>
            </MessageBar>

            {/* Input mode selection */}
            <Pivot
                selectedKey={inputMode}
                onLinkClick={handleModeChange}
                headersOnly
            >
                <PivotItem itemKey="csv" headerText="Upload CSV" />
                <PivotItem itemKey="paste" headerText="Paste IDs" />
            </Pivot>

            {/* CSV drop zone */}
            {inputMode === 'csv' && (
                <div
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    style={{
                        border: `2px dashed ${
                            isDragging
                                ? theme.palette.themePrimary
                                : theme.palette.neutralTertiaryAlt
                        }`,
                        borderRadius: '8px',
                        padding: '32px 16px',
                        textAlign: 'center',
                        cursor: 'pointer',
                        backgroundColor: isDragging
                            ? theme.palette.themeLighterAlt
                            : theme.palette.neutralLighterAlt,
                        transition: 'all 0.2s ease',
                    }}
                >
                    <Icon
                        iconName={fileName ? 'CheckMark' : 'CloudUpload'}
                        styles={{
                            root: {
                                fontSize: 36,
                                color: fileName
                                    ? theme.palette.green
                                    : theme.palette.themePrimary,
                                marginBottom: '8px',
                                display: 'block',
                            },
                        }}
                    />
                    {fileName ? (
                        <Text
                            variant="medium"
                            styles={{ root: { fontWeight: 600 } }}
                        >
                            {fileName}
                        </Text>
                    ) : (
                        <>
                            <Text
                                variant="medium"
                                styles={{ root: { fontWeight: 600 } }}
                            >
                                Drag &amp; drop a dgrep CSV export here
                            </Text>
                            <Text
                                variant="small"
                                styles={{
                                    root: {
                                        color: theme.palette.neutralSecondary,
                                        display: 'block',
                                        marginTop: '4px',
                                    },
                                }}
                            >
                                or click to browse. The CSV must contain a
                                &quot;MessageId&quot; column.
                            </Text>
                        </>
                    )}
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv"
                        onChange={handleFileSelect}
                        style={{ display: 'none' }}
                    />
                </div>
            )}

            {/* Paste IDs */}
            {inputMode === 'paste' && (
                <div
                    style={{
                        padding: '12px',
                        backgroundColor: theme.palette.white,
                        borderRadius: '4px',
                        border: `1px solid ${theme.palette.neutralTertiaryAlt}`,
                    }}
                >
                    <TextField
                        label="Message Ids"
                        placeholder={
                            'Paste message ids separated by new lines, commas, or spaces.\nExample:\n123\n456\n789'
                        }
                        multiline
                        rows={8}
                        value={pasteText}
                        onChange={handlePasteChange}
                        borderless
                    />
                </div>
            )}

            {/* File read error (CSV mode only) */}
            {inputMode === 'csv' && fileReadError && (
                <MessageBar messageBarType={MessageBarType.error}>
                    {fileReadError}
                </MessageBar>
            )}

            {/* Parse error from the active input */}
            {activeResult?.error && (
                <MessageBar messageBarType={MessageBarType.error}>
                    {activeResult.error}
                </MessageBar>
            )}

            {/* Parse summary */}
            {activeResult && !activeResult.error && (
                <MessageBar messageBarType={MessageBarType.success}>
                    {`Found ${ids.length} unique message id${
                        ids.length === 1 ? '' : 's'
                    } to replay`}
                    {activeResult.duplicatesRemoved > 0
                        ? ` (${activeResult.duplicatesRemoved} duplicate${
                              activeResult.duplicatesRemoved === 1 ? '' : 's'
                          } removed).`
                        : '.'}
                </MessageBar>
            )}

            {/* Batch-size warning */}
            {ids.length > RECOMMENDED_MAX_IDS && (
                <MessageBar messageBarType={MessageBarType.warning}>
                    {`This contains ${ids.length} ids. It is recommended to replay no more than ${RECOMMENDED_MAX_IDS} at a time. Consider splitting into smaller batches.`}
                </MessageBar>
            )}

            <Stack
                horizontal
                verticalAlign="center"
                tokens={{ childrenGap: 12 }}
            >
                <PrimaryButton
                    text={isLoading ? 'Replaying...' : 'Replay Messages'}
                    iconProps={{ iconName: 'Rocket' }}
                    onClick={handleReplayClick}
                    disabled={isLoading || ids.length === 0}
                    styles={{
                        root: {
                            backgroundColor: theme.palette.themePrimary,
                            borderColor: theme.palette.themePrimary,
                        },
                        rootHovered: {
                            backgroundColor: theme.palette.themeDark,
                            borderColor: theme.palette.themeDark,
                        },
                    }}
                />
                {isLoading && (
                    <Spinner size={SpinnerSize.medium} label="Replaying..." />
                )}
            </Stack>

            {/* Replay error */}
            {error && !isLoading && (
                <MessageBar messageBarType={MessageBarType.error}>
                    Error: {error.message ?? 'The replay request failed.'}
                </MessageBar>
            )}

            {/* Replay result */}
            {result && !isLoading && (
                <Stack tokens={{ childrenGap: 16 }}>
                    {/* Summary Cards */}
                    <Stack horizontal tokens={{ childrenGap: 12 }} wrap>
                        <div
                            style={{
                                padding: '12px',
                                backgroundColor: theme.palette.neutralLighter,
                                borderRadius: '4px',
                                minWidth: '150px',
                                flex: '1 1 auto',
                            }}
                        >
                            <Text
                                variant="small"
                                styles={{
                                    root: {
                                        color: theme.palette.neutralSecondary,
                                        fontWeight: 600,
                                    },
                                }}
                            >
                                Successful
                            </Text>
                            <Text
                                variant="xLarge"
                                styles={{
                                    root: {
                                        color: theme.palette.green,
                                        fontWeight: 600,
                                        display: 'block',
                                    },
                                }}
                            >
                                {result.data?.successfulCount ?? 0}
                            </Text>
                        </div>

                        <div
                            style={{
                                padding: '12px',
                                backgroundColor:
                                    (result.data?.unsuccessfulCount ?? 0) > 0
                                        ? theme.palette.redDark
                                        : theme.palette.neutralLighter,
                                borderRadius: '4px',
                                minWidth: '150px',
                                flex: '1 1 auto',
                            }}
                        >
                            <Text
                                variant="small"
                                styles={{
                                    root: {
                                        color:
                                            (result.data?.unsuccessfulCount ??
                                                0) > 0
                                                ? theme.palette.white
                                                : theme.palette
                                                      .neutralSecondary,
                                        fontWeight: 600,
                                    },
                                }}
                            >
                                Unsuccessful
                            </Text>
                            <Text
                                variant="xLarge"
                                styles={{
                                    root: {
                                        color:
                                            (result.data?.unsuccessfulCount ??
                                                0) > 0
                                                ? theme.palette.white
                                                : theme.palette.neutralPrimary,
                                        fontWeight: 600,
                                        display: 'block',
                                    },
                                }}
                            >
                                {result.data?.unsuccessfulCount ?? 0}
                            </Text>
                        </div>
                    </Stack>

                    {/* Status message */}
                    <MessageBar
                        messageBarType={
                            result.succeeded
                                ? MessageBarType.success
                                : MessageBarType.warning
                        }
                    >
                        {result.message ??
                            (result.succeeded
                                ? 'All messages replayed successfully.'
                                : 'Some messages could not be replayed.')}
                    </MessageBar>

                    {/* Unsuccessful ids */}
                    {result.data?.unsuccessful &&
                        result.data.unsuccessful.length > 0 && (
                            <div
                                style={{
                                    padding: '16px',
                                    backgroundColor: theme.palette.white,
                                    borderRadius: '4px',
                                    border: `1px solid ${theme.palette.neutralTertiaryAlt}`,
                                }}
                            >
                                <Text
                                    variant="large"
                                    styles={{
                                        root: {
                                            fontWeight: 600,
                                            marginBottom: '12px',
                                            display: 'block',
                                        },
                                    }}
                                >
                                    Unsuccessful Message Ids
                                </Text>
                                <div
                                    style={{
                                        maxHeight: '200px',
                                        overflowY: 'auto',
                                        fontFamily: 'monospace',
                                        fontSize: '12px',
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-all',
                                        color: theme.palette.neutralPrimary,
                                    }}
                                >
                                    {result.data.unsuccessful.join('\n')}
                                </div>
                            </div>
                        )}
                </Stack>
            )}
        </Stack>
    );
};
